"use strict";

var _slicedToArray = require("/home/circleci/project/web-front/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/slicedToArray");
var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault").default;
var _interopRequireWildcard = require("@babel/runtime/helpers/interopRequireWildcard").default;
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
exports.useRichTextEditorFunctions = useRichTextEditorFunctions;
var _react = _interopRequireWildcard(require("react"));
var _styledComponents = _interopRequireDefault(require("styled-components"));
var _plateCommon = require("@udecode/plate-common");
var _tooltip = require("../../../../plate-js/plate-ui/tooltip");
var _fixedToolbar = require("../../../../plate-js/plate-ui/fixed-toolbar");
var _fixedToolbarButtons = require("../../../../plate-js/plate-ui/fixed-toolbar-buttons");
var _editorComponent = require("../../../../plate-js/plate-ui/editor-component");
var _RichTextEditorConfiguration = require("./RichTextEditorConfiguration");
var _plateCore = require("@udecode/plate-core");
var _mentionContext = require("../../../../plate-js/lib/mentionContext");
var _jsxRuntime = require("react/jsx-runtime");
/**
 * Based on platejs
 *
 * Accepts input in the form of slate nodes or html
 * Outputs html via onBlur and slate nodes via onChange
 * Additionally, two functions to reset and get Html are provided via the RichTextEditorFunctionsType forward ref
 *
 * The active features are toggled via pluginToggles
 *
 * We are stuck on v36 until we can upgrade react to 18
 * V36 documentation: https://v36.platejs.org/docs
 * The CLI tool does not generate correct files for v36 so the controls are manually copied from the website
 * Due to an unidentified issue with js packaging, we also need to copy any behavioral hooks used by the controls
 * from the v36 git repository: https://github.com/udecode/plate/tree/v36
 *
 * The configuration is in RichTextEditorConfiguration.ts - check the commented out plugins list for a list of available plugins
 *
 * If, for some reason, you need to update the tailwind styles, please run this command:
 * "npx tailwindcss -i ./src/plate-js/plateStyles.css -o ./public/plateStylesCompiled.scss"
 *  This will generate a new tailwind file in ./src/public/
 *  Now examine the diff and make sure to recreate the manual changes that added .plate-editor-wrapper around individual element
 *  styles as well as replacing global elements (hmll, body, root etc.) with .plate-editor-wrapper
 *  These wrappers of course make the css an scss file that needs to be compiled again
 *  use the plate-css-flatten npm script for that
 *  Finally the css files need to be copied to web-front public folder manually.
 *  We can automate this in the future, but the global css needs to be loaded in the web-front index.html file
 *
 *  Custom components:
 *  Most of the components and hooks in the plate-js folder are copied from the Plate.js website and repo, however some are heavily modified or created from scratch:
 *
 *  the entire contents of the lib folder is custom
 *    - some deserializer customization
 *    - the mentionContext and the withIndestructibleElements helper used to make the non-editable-element actually be non-editable and non-deletable
 *  plate-ui:
 *    - non-editable-element.tsx (render the non editable jira content)
 *    - mention-input-element.tsx (modified to use the mentionContext)
 *  hooks:
 *    - use-click-outside.ts (for custom on-blur handling)
 *  forks:
 *    All copied
 *  auto-format:
 *    configuration file for auto-format - can probably be reused in future versions
 * */
var PlateEditorWrapper = _styledComponents.default.div.withConfig({
  displayName: "RichTextEditor__PlateEditorWrapper",
  componentId: "sc-9qs8i3-0"
})(["", ";color:", ";"], function (_ref) {
  var theme = _ref.theme;
  return theme.sys.font.body['1'];
}, function (_ref2) {
  var theme = _ref2.theme;
  return theme.sys.color.text.content.main;
});
var defaultPluginToggles = {
  todoList: false,
  paragraphStyles: false,
  link: false
};
var RichTextEditor = /*#__PURE__*/_react.default.forwardRef(function (props, ref) {
  var initialValue = props.initialValue,
    initialHtml = props.initialHtml,
    onChange = props.onChange,
    onBlur = props.onBlur,
    peopleMentions = props.peopleMentions,
    labelMentions = props.labelMentions,
    _props$pluginToggles = props.pluginToggles,
    pluginToggles = _props$pluginToggles === void 0 ? defaultPluginToggles : _props$pluginToggles,
    dataCy = props['data-cy'],
    _props$id = props.id,
    id = _props$id === void 0 ? 'plate' : _props$id,
    _props$hideToolbarUnt = props.hideToolbarUntilFocused,
    hideToolbarUntilFocused = _props$hideToolbarUnt === void 0 ? true : _props$hideToolbarUnt,
    _props$toolbarPositio = props.toolbarPosition,
    toolbarPosition = _props$toolbarPositio === void 0 ? 'bottom' : _props$toolbarPositio,
    placeholder = props.placeholder,
    _props$readOnly = props.readOnly,
    readOnly = _props$readOnly === void 0 ? false : _props$readOnly,
    _props$variant = props.variant,
    variant = _props$variant === void 0 ? 'default' : _props$variant;
  var plugins = (0, _react.useMemo)(function () {
    return (0, _RichTextEditorConfiguration.getMinimalPlugins)(pluginToggles);
  }, [pluginToggles]);
  var _ref3 = (0, _react.useState)(false),
    _ref4 = _slicedToArray(_ref3, 2),
    isFocused = _ref4[0],
    setFocused = _ref4[1];
  var showToolbar = (0, _react.useMemo)(function () {
    return (!hideToolbarUntilFocused || isFocused) && !readOnly;
  }, [hideToolbarUntilFocused, isFocused, readOnly]);
  var initialValueFromHtml = (0, _react.useMemo)(function () {
    var localEditor = (0, _plateCommon.createPlateEditor)({
      plugins: plugins
    });
    if (!initialHtml || (initialHtml === null || initialHtml === void 0 ? void 0 : initialHtml.trim()) === '') {
      return undefined;
    }
    return (0, _plateCore.deserializeHtml)(localEditor, {
      element: initialHtml || ''
    });
  }, [initialHtml, plugins]);

  // deserializeHtml output is not 100% type compatible with initialValue, so we need to cast it
  // this is one of those things that need to be revisited when upgrading to the latest version of Plate
  var editorValue = initialValueFromHtml || initialValue;
  var onChangeHandler = (0, _react.useCallback)(function (value) {
    onChange === null || onChange === void 0 ? void 0 : onChange(value);
  }, [onChange]);
  var onBlurHandler = function onBlurHandler(html) {
    onBlur === null || onBlur === void 0 ? void 0 : onBlur(html);
  };
  var wrapperRef = (0, _react.useRef)(null);
  return (/*#__PURE__*/
    /* PlateStoreProvider should not be necessary here, according to the docs, but it forces the plate store
     * to be initialized before the Plate component and this makes it work in web-front */
    (0, _jsxRuntime.jsx)(PlateEditorWrapper, {
      "data-cy": dataCy + '-container',
      className: 'plate-editor-wrapper',
      ref: wrapperRef,
      children: /*#__PURE__*/(0, _jsxRuntime.jsx)(_mentionContext.MentionProvider, {
        children: /*#__PURE__*/(0, _jsxRuntime.jsx)(_plateCore.PlateStoreProvider, {
          id: 'plate',
          children: /*#__PURE__*/(0, _jsxRuntime.jsx)(_tooltip.TooltipProvider, {
            children: /*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
              className: variant === 'noBorder' ? ' rounded-md border-0' : ' rounded-md border border-input',
              onClick: function onClick() {
                return setFocused(true);
              },
              children: /*#__PURE__*/(0, _jsxRuntime.jsxs)(_plateCommon.Plate, {
                id: id,
                plugins: plugins,
                initialValue: editorValue,
                onChange: onChangeHandler,
                readOnly: readOnly,
                children: [toolbarPosition === 'top' && showToolbar && /*#__PURE__*/(0, _jsxRuntime.jsx)(_fixedToolbar.FixedToolbarTop, {
                  children: /*#__PURE__*/(0, _jsxRuntime.jsx)(_fixedToolbarButtons.FixedToolbarButtons, {
                    pluginToggles: pluginToggles
                  })
                }), /*#__PURE__*/(0, _jsxRuntime.jsx)(_editorComponent.EditorComponent, {
                  placeholder: placeholder,
                  focusRing: false,
                  size: "md",
                  onBlurHandler: onBlurHandler,
                  peopleMentions: peopleMentions,
                  labelMentions: labelMentions,
                  "data-cy": dataCy,
                  ref: ref,
                  variant: variant,
                  wrapperRef: wrapperRef,
                  readOnly: readOnly
                }), toolbarPosition === 'bottom' && showToolbar && /*#__PURE__*/(0, _jsxRuntime.jsx)(_fixedToolbar.FixedToolbarBottom, {
                  className: 'border-t border-t-border rounded-b-lg',
                  children: /*#__PURE__*/(0, _jsxRuntime.jsx)(_fixedToolbarButtons.FixedToolbarButtons, {
                    pluginToggles: pluginToggles
                  })
                })]
              })
            })
          })
        })
      })
    })
  );
});
function useRichTextEditorFunctions() {
  var editorRef = (0, _react.useRef)(null);
  var resetEditorContent = function resetEditorContent(newHtml) {
    var _editorRef$current;
    (_editorRef$current = editorRef.current) === null || _editorRef$current === void 0 ? void 0 : _editorRef$current.reset(newHtml);
  };
  var getEditorValueAsHtml = function getEditorValueAsHtml() {
    var _editorRef$current2;
    return (_editorRef$current2 = editorRef.current) === null || _editorRef$current2 === void 0 ? void 0 : _editorRef$current2.getHtmlValue();
  };
  return {
    editorRef: editorRef,
    resetEditorContent: resetEditorContent,
    getEditorValueAsHtml: getEditorValueAsHtml
  };
}
var _default = exports.default = RichTextEditor;